import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client/react";
import { ArrowLeft, Loader2, User, BookOpen } from "lucide-react";

import { GET_STUDENTS } from "@/graphql/queries/students";
import { GET_SUBJECTS } from "@/graphql/queries/subjects";
import { CREATE_SESSION, GENERATE_EXERCISES } from "@/graphql/mutations/sessions";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { SubjectIcon } from "@/components/subject-icon";
import { ExerciseCountField } from "@/components/exercise-count-field";
import { exercisesLabel, parseExerciseCount, EXERCISE_COUNT_FALLBACK } from "@/lib/students";

export default function NewSessionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedStudentId = searchParams.get("studentId");
  const preselectedSubjectId = searchParams.get("subjectId");

  const [selectedStudent, setSelectedStudent] = useState<string>(preselectedStudentId || "");
  const [selectedSubject, setSelectedSubject] = useState<string>(preselectedSubjectId || "");
  // How long the session should be, as typed. `null` means untouched, so this
  // learner's own length decides — which is what the server does with no number.
  const [countDraft, setCountDraft] = useState<string | null>(null);

  const { data: studentsData, loading: studentsLoading } = useQuery(GET_STUDENTS);
  const { data: subjectsData, loading: subjectsLoading } = useQuery(GET_SUBJECTS);

  const [createSession, { loading: isCreating }] = useMutation(CREATE_SESSION);
  const [generateExercises, { loading: isGenerating }] = useMutation(GENERATE_EXERCISES);

  const students: {
    id: string;
    name: string;
    age?: number;
    suggestedExerciseCount?: number;
  }[] = studentsData?.students ?? [];
  const subjects: { id: string; name: string; icon: string }[] = subjectsData?.subjects ?? [];

  const student = students.find((entry) => entry.id === selectedStudent);
  const suggestedCount = student?.suggestedExerciseCount ?? EXERCISE_COUNT_FALLBACK;
  const requestedCount = parseExerciseCount(countDraft ?? String(suggestedCount));

  const isLoading = studentsLoading || subjectsLoading;
  const isWorking = isCreating || isGenerating;

  async function handleCreate() {
    if (!selectedStudent) {
      toast.error("Pasirinkite mokinį");
      return;
    }

    if (!selectedSubject) {
      toast.error("Pasirinkite dalyką");
      return;
    }

    let sessionId: string;
    try {
      const { data } = await createSession({
        variables: { studentId: selectedStudent, subjectKey: selectedSubject },
      });
      sessionId = data.createSession.session.id;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nepavyko sukurti sesijos");
      return;
    }

    // The length is a generation argument, so it would mean nothing if the form
    // only stored it: the session is generated here, with the number that was
    // just typed. A generation that fails still leaves the draft, and the
    // session page can retry it with the full set of options.
    try {
      const { data } = await generateExercises({
        variables: { sessionId, targetExerciseCount: requestedCount ?? undefined },
      });
      const errors: string[] = data?.generateExercises?.errors ?? [];
      if (errors.length > 0) {
        toast.error(errors.join("; "));
      } else {
        const generated = data?.generateExercises?.generatedCount ?? 0;
        toast.success(`Sesija sukurta — ${exercisesLabel(generated)}`);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nepavyko sugeneruoti užduočių");
    }

    navigate(`/sessions/${sessionId}`);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/sessions">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Nauja sesija</h1>
          <p className="text-muted-foreground">
            Sukurkite naują mokymosi sesiją mokiniui
          </p>
        </div>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Sesijos nustatymai</CardTitle>
          <CardDescription>
            Pasirinkite mokinį ir dalyką, kuriam norite sukurti pratimus
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {students.length === 0 ? (
            <div className="text-center py-6">
              <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                Nėra mokinių. Pirmiausia pridėkite mokinį.
              </p>
              <Button asChild>
                <Link to="/students/new">Pridėti mokinį</Link>
              </Button>
            </div>
          ) : subjects.length === 0 ? (
            <div className="text-center py-6">
              <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                Nėra dalykų. Patikrinkite, ar įgūdžiai sinchronizuoti.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="student">Mokinys *</Label>
                <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                  <SelectTrigger id="student">
                    <SelectValue placeholder="Pasirinkite mokinį" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map((student) => (
                      <SelectItem key={student.id} value={student.id}>
                        {student.name} ({student.age} m.)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Dalykas *</Label>
                <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                  <SelectTrigger id="subject">
                    <SelectValue placeholder="Pasirinkite dalyką" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjects.map((subject) => (
                      <SelectItem key={subject.id} value={subject.id}>
                        <span className="flex items-center gap-2">
                          <SubjectIcon icon={subject.icon} className="h-4 w-4" />
                          {subject.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <ExerciseCountField
                draft={countDraft}
                onDraftChange={setCountDraft}
                suggested={suggestedCount}
                disabled={isWorking}
              />

              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleCreate}
                  disabled={isWorking || !selectedStudent || !selectedSubject}
                >
                  {isWorking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isGenerating ? "Generuojama..." : "Sukurti sesiją"}
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/sessions">Atšaukti</Link>
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
